import { CAPTION_MODELS, PUBLISHED_MODEL_FILES } from '@/config/asset-paths';
import type { AsrQuality } from '@/core/captions/types';
import { fetchCached, ModelCache } from '@/lib/model-cache';

const ownModels = new Set<string>(PUBLISHED_MODEL_FILES);
const captionModels = Object.values(CAPTION_MODELS);

/**
 * Whether a cached address is still one the app uses: our own models by
 * name, caption models by repository. Runtime files are replaced by their
 * newer versions as they are stored, and anything else is left alone.
 */
export function isCurrentModel(url: URL): boolean {
  const own = /\/models\/([^/]+)$/.exec(url.pathname);
  if (own) return ownModels.has(own[1]!);
  if (url.hostname === 'huggingface.co' || url.hostname.endsWith('.huggingface.co')) return captionModels.some((id) => url.pathname.startsWith(`/${id}/`));
  return true;
}

/**
 * Models and engine binaries kept on this device (see `lib/model-cache.ts`).
 * `fetch` is read at call time, so the privacy ledger counts real downloads only.
 */
export const modelCache = new ModelCache({
  caches: typeof caches === 'undefined' ? undefined : caches,
  fetch: (...args) => fetch(...args),
  now: () => Date.now(),
  isCurrent: isCurrentModel,
});

/** The bytes of a model or engine file, from the device when it has them. */
export const fetchModelFile = (url: string, validate?: (bytes: Uint8Array) => boolean, fetcher: typeof fetch = (...args) => fetch(...args)): Promise<Uint8Array> =>
  fetchCached(url, modelCache, { fetch: fetcher }, validate);

/** ONNX files are protocol buffers whose first field is the IR version (tag byte 0x08). */
export const looksLikeOnnx = (bytes: Uint8Array): boolean => bytes.length > 16 && bytes[0] === 0x08;

/** The two halves of a Whisper model (each at whichever precision the device runs, see analysis/transcribe.ts). */
const CAPTION_MODEL_PARTS = ['encoder_model', 'decoder_model_merged'] as const;

/** Whether this device already keeps a caption model's weights, so writing captions downloads nothing big. */
export async function keepsCaptionModel(quality: AsrQuality, cache: Pick<ModelCache, 'kept'> = modelCache): Promise<boolean> {
  const folder = `/${CAPTION_MODELS[quality]}/`;
  const weights = (await cache.kept()).filter((url) => url.pathname.startsWith(folder) && url.pathname.endsWith('.onnx'));
  return CAPTION_MODEL_PARTS.every((part) => weights.some((url) => url.pathname.includes(`/${part}`)));
}
